/*
 * Copyright (c) 2023 Bluemate B.V.
 */

import React from 'react';
import {SelectItemShape} from '@dvrd/dvr-controls';
import EmailStep from '../models/emailStepModel';
import {CustomerType, EmailFlow, Fulfilment, UnlayerDesign} from './interfaces';
import EmailTemplate from '../models/emailTemplateModel';
import Account from '../models/accountModel';
import InfoIcon from '../components/icon/infoIcon';
import {ProductSelectionEnum} from './enums';
import {ProductSelectionClauseType} from './types';
import i18n from '../i18n';

export type TemplateParams = {name: string; subject: string; json: UnlayerDesign};

const dayItemsCache = new Map<string, SelectItemShape[]>();
i18n.on('languageChanged', () => {
    dayItemsCache.clear();
});

function genDayItems(step: EmailStep): SelectItemShape[] {
    const lng = i18n.resolvedLanguage ?? i18n.language ?? '';
    const cacheKey = `${step.onlyWorkDays ? 1 : 0}|${step.flow}|${lng}`;
    const cached = dayItemsCache.get(cacheKey);
    if (cached) return cached;
    const daysLabel = step.onlyWorkDays
        ? i18n.t('util.emailStep.workDay', 'werkdag')
        : i18n.t('util.emailStep.day', 'dag');
    const daysPluralLabel = step.onlyWorkDays
        ? i18n.t('util.emailStep.workDays', 'werkdagen')
        : i18n.t('util.emailStep.days', 'dagen');
    const {flow} = step;
    let sevenDays: SelectItemShape;
    if (flow === EmailFlow.REVIEW_INVOICE)
        sevenDays = {
            value: 7,
            label: (
                <label className='recommend-days'>
                    {i18n.t('util.emailStep.nDaysAfterShipment', '{{amount}} {{days}} na zending', {
                        amount: 7,
                        days: daysPluralLabel
                    })}{' '}
                    <span
                        style={{height: '28px'}}
                        title={i18n.t(
                            'util.emailStep.reviewInvoiceTooltip',
                            'Voor beste conversie, verzend je Review + Factuur mail minimaal 7 dagen na zending. Dan heeft je klant de bestelling waarschijnlijk ontvangen en kunnen testen.'
                        )}
                    >
                        <InfoIcon />
                    </span>
                </label>
            ),
            valueLabel: i18n.t('util.emailStep.nDaysAfterShipment', '{{amount}} {{days}} na zending', {
                amount: 7,
                days: daysPluralLabel
            })
        };
    else
        sevenDays = {
            value: 7,
            label: i18n.t('util.emailStep.nDaysAfterShipment', '{{amount}} {{days}} na zending', {
                amount: 7,
                days: daysPluralLabel
            })
        };
    const value: SelectItemShape[] = [
        {value: 0, label: i18n.t('util.emailStep.orderSent', 'Order verzonden')},
        {
            value: 1,
            label: i18n.t('util.emailStep.nDaysAfterShipment', '{{amount}} {{days}} na zending', {
                amount: 1,
                days: daysLabel
            })
        },
        {
            value: 3,
            label: i18n.t('util.emailStep.nDaysAfterShipment', '{{amount}} {{days}} na zending', {
                amount: 3,
                days: daysPluralLabel
            })
        },
        sevenDays,
        {
            value: 14,
            label: i18n.t('util.emailStep.nDaysAfterShipment', '{{amount}} {{days}} na zending', {
                amount: 14,
                days: daysPluralLabel
            })
        }
    ];
    dayItemsCache.set(cacheKey, value);
    return value;
}

export function getDayItems(step: EmailStep, account: Account | null): SelectItemShape[] {
    const items = genDayItems(step).slice();
    if (!step.withInvoice || account?.getFlex('override_email_limitations'))
        items.splice(0, 0, {
            value: -1,
            label: i18n.t('util.emailStep.orderReceived', 'Order ontvangen'),
            selectable: false
        });
    let daysLabel = step.onlyWorkDays
        ? i18n.t('util.emailStep.workDay', 'werkdag')
        : i18n.t('util.emailStep.day', 'dag');
    if (step.days > 1)
        daysLabel = step.onlyWorkDays
            ? i18n.t('util.emailStep.workDays', 'werkdagen')
            : i18n.t('util.emailStep.days', 'dagen');
    return items.concat([
        {
            value: 'custom',
            label: i18n.t('util.emailStep.custom', 'Aangepast'),
            valueLabel: i18n.t('util.emailStep.nDaysAfterShipment', '{{amount}} {{days}} na zending', {
                amount: step.days,
                days: daysLabel
            })
        }
    ]);
}

export const getCustomerTypeItems = (): SelectItemShape[] => [
    {value: CustomerType.BUSINESS, label: i18n.t('util.emailStep.customerBusiness', 'Zakelijke klanten')},
    {value: CustomerType.PRIVATE, label: i18n.t('util.emailStep.customerPrivate', 'Particuliere klanten')},
    {value: CustomerType.BOTH, label: i18n.t('util.emailStep.customerAll', 'Alle klanten')}
];

export function getStepTitle(step: EmailStep): string {
    switch (step.flow) {
        case EmailFlow.INVOICE:
            return i18n.t('util.emailStep.title.invoice', 'FACTUUR MAIL');
        case EmailFlow.OTHER:
            return i18n.t('util.emailStep.title.other', 'ANDERE MAIL');
        case EmailFlow.PRODUCT:
            return i18n.t('util.emailStep.title.product', 'PRODUCT MAIL');
        case EmailFlow.REVIEW:
            return i18n.t('util.emailStep.title.review', 'REVIEW MAIL');
        case EmailFlow.REVIEW_INVOICE:
            return i18n.t('util.emailStep.title.reviewInvoice', 'REVIEW + FACTUUR MAIL');
        case EmailFlow.CREDIT_INVOICE:
            return i18n.t('util.emailStep.title.creditInvoice', 'MAIL MET CREDITFACTUUR');
    }
}

export function getTemplateLabel(step: EmailStep, templates: EmailTemplate[]): string {
    return (
        templates.find(template => template.id === step.templateId)?.name ??
        i18n.t('util.emailStep.chooseTemplate', 'Kies template')
    );
}

export function getProductsLabel(
    clauses: Array<ProductSelectionClauseType>,
    productSelection: ProductSelectionEnum,
    numProducts: number,
    active?: boolean
): string {
    if (!clauses || productSelection === ProductSelectionEnum.NONE)
        return i18n.t('util.emailStep.chooseProducts', 'Kies product(en)');
    else if (productSelection === ProductSelectionEnum.ALL)
        return i18n.t('util.emailStep.allProducts', 'Alle producten');
    else if (productSelection === ProductSelectionEnum.REMAINING) {
        if (numProducts > -1 && active !== false)
            return i18n.t('util.emailStep.remainingProductsCount', 'Resterende {{count}} producten', {
                count: numProducts
            });
        return i18n.t('util.emailStep.remainingProducts', 'Resterende producten');
    } else if (numProducts === -1) {
        return i18n.t('util.emailStep.selectedProducts', 'Geselecteerde producten');
    }
    return i18n.t('util.emailStep.productsCount', '{{count}} producten', {count: numProducts});
}

export function getAttachmentsLabel(step: EmailStep): string {
    const {attachments, withInvoice} = step;
    const totalAttachments = attachments.length + (withInvoice ? 1 : 0);
    if (!totalAttachments) return i18n.t('util.emailStep.none', 'Geen');
    else if (totalAttachments === 1) {
        if (withInvoice)
            return step.flow === EmailFlow.CREDIT_INVOICE
                ? i18n.t('util.emailStep.attachmentCreditInvoice', 'Creditfactuur.pdf')
                : i18n.t('util.emailStep.attachmentInvoice', 'Factuur.pdf');
        else return attachments[0].name;
    } else return i18n.t('util.emailStep.attachmentsCount', '{{count}} bijlages', {count: totalAttachments});
}

export function getCustomersLabel(step: EmailStep): string {
    if (step.customerType === CustomerType.BUSINESS)
        return i18n.t('util.emailStep.customerBusiness', 'Zakelijke klanten');
    if (step.customerType === CustomerType.PRIVATE)
        return i18n.t('util.emailStep.customerPrivate', 'Particuliere klanten');
    return i18n.t('util.emailStep.customerAll', 'Alle klanten');
}

export function getCountriesLabel(step: EmailStep): string {
    const count = step.countries.length;
    if (!count) return i18n.t('util.emailStep.allCountries', 'Alle landen');
    if (count === 1) return step.countries[0];
    return i18n.t('util.emailStep.countriesCount', '{{count}} landen', {count});
}

export function getLogisticsLabel(step: EmailStep): string {
    if (step.fulfilment === Fulfilment.FBB) return i18n.t('util.emailStep.logisticsLvb', 'LVB');
    if (step.fulfilment === Fulfilment.FBR) return i18n.t('util.emailStep.logisticsOwn', 'Eigen logistiek');
    return i18n.t('util.emailStep.logisticsAll', 'Alle logistiek');
}

export type FlowParams = {title: string; description: string};

export function getFlowParams(flow: EmailFlow): FlowParams | null {
    switch (flow) {
        case EmailFlow.INVOICE:
            return {
                title: i18n.t('util.emailStep.flow.invoice.title', 'Factuur mail'),
                description: i18n.t(
                    'util.emailStep.flow.invoice.description',
                    'Een mail met de BTW factuur als bijlage'
                )
            };
        case EmailFlow.REVIEW_INVOICE:
            return {
                title: i18n.t('util.emailStep.flow.reviewInvoice.title', 'Review + Factuur mail (aanbevolen)'),
                description: i18n.t(
                    'util.emailStep.flow.reviewInvoice.description',
                    'Vraag om een productbeoordeling inclusief link en voeg de factuur toe als bijlage.'
                )
            };
        case EmailFlow.REVIEW:
            return {
                title: i18n.t('util.emailStep.flow.review.title', 'Review mail'),
                description: i18n.t('util.emailStep.flow.review.description', 'Vraag om een productreview op Bol.')
            };
        case EmailFlow.PRODUCT:
            return {
                title: i18n.t('util.emailStep.flow.product.title', 'Productniveau mail'),
                description: i18n.t(
                    'util.emailStep.flow.product.description',
                    'Verstuur een mail alleen bij bepaalde bestelde producten. Bijvoorbeeld een product handleiding mail met bijlage.'
                )
            };
        case EmailFlow.OTHER:
            return {
                title: i18n.t('util.emailStep.flow.other.title', 'Andere mail'),
                description: i18n.t('util.emailStep.flow.other.description', 'Stel zelf een ander soort mail samen.')
            };
    }
    return null;
}
